<template>
  <li class="headline">
    {{ t('menu.tabs.options') }}
  </li>
  <li v-if="players.length" @click="userPreferences.hideGrim = !userPreferences.hideGrim">
    {{ userPreferences.hideGrim ? t('menu.show') : t('menu.hide') }}
    <em>[G]</em>
  </li>
  <li v-if="session.isPlayerOrSpectator"
    @click="userPreferences.orderBubblesAsPlayer = !userPreferences.orderBubblesAsPlayer">
    {{ t('menu.order') }}
    <em>
      <font-awesome-icon :icon="[
        'fas',
        userPreferences.orderBubblesAsPlayer ? 'check-square' : 'square',
      ]" />
    </em>
  </li>
  <li v-else @click="userPreferences.orderBubblesAsHost = !userPreferences.orderBubblesAsHost">
    {{ t('menu.order') }}
    <em>
      <font-awesome-icon :icon="[
        'fas',
        userPreferences.orderBubblesAsHost ? 'check-square' : 'square',
      ]" />
    </em>
  </li>
  <li v-if="players.length">
    {{ t('menu.zoom') }}
    <em>
      <font-awesome-icon icon="search-minus" class="fa fa-search-minus"
        @click="userPreferences.zoom = Math.max(userPreferences.zoom - 1, -5)" />
      {{ Math.round(100 + userPreferences.zoom * 10) }}%
      <font-awesome-icon icon="search-plus" class="fa fa-search-plus"
        @click="userPreferences.zoom = Math.min(userPreferences.zoom + 1, 10)" />
    </em>
  </li>
  <li v-if="!session.isPlayerOrSpectator">
    {{ t('menu.language') }}
    <em>
      <font-awesome-icon icon="chevron-left" class="fa fa-chevron-left" @click="previousLanguage" />
      {{ locale.currentLanguage.toUpperCase() }}
      <font-awesome-icon icon="chevron-right" class="fa fa-chevron-right" @click="nextLanguage" />
    </em>
  </li>
  <li @click="setBackground()">
    {{ t('menu.background') }}
    <em><font-awesome-icon icon="image" class="fa fa-image" /></em>
  </li>
  <li v-if="!grimoire.edition?.isOfficial" @click="imageOptIn">
    <small>{{ t('menu.customImages') }}</small>
    <em><font-awesome-icon :icon="[
      'fas',
      userPreferences.isImageOptIn ? 'check-square' : 'square',
    ]" /></em>
  </li>
  <li @click="userPreferences.isStreamerMode = !userPreferences.isStreamerMode">
    <small>{{ t('menu.streamerMode') }}</small>
    <em><font-awesome-icon :icon="[
      'fas',
      userPreferences.isStreamerMode ? 'check-square' : 'square',
    ]" /></em>
  </li>
  <li @click="userPreferences.notes.hidden = !userPreferences.notes.hidden">
    <small>{{ t('menu.notes') }}</small>
    <em><font-awesome-icon :icon="[
      'fas',
      userPreferences.notes.hidden ? 'square' : 'check-square',
    ]" /></em>
  </li>
  <li @click="userPreferences.isStatic = !userPreferences.isStatic">
    {{ t('menu.animations') }}
    <em><font-awesome-icon :icon="['fas', userPreferences.isStatic ? 'check-square' : 'square']" /></em>
  </li>
  <li @click="userPreferences.isMuted = !userPreferences.isMuted">
    {{ t('menu.mute') }}
    <em><font-awesome-icon :icon="['fas', userPreferences.isMuted ? 'volume-mute' : 'volume-up']" /></em>
  </li>
</template>

<script setup lang="ts">
import {
  useDialogStore,
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useSessionStore,
  useUserPreferencesStore
} from "@/stores";
import { computed } from "vue";

const dialogStore = useDialogStore();
const grimoire = useGrimoireStore();
const locale = useLocaleStore();
const playersStore = usePlayersStore();
const t = locale.t;
const session = useSessionStore();
const userPreferences = useUserPreferencesStore();

const players = computed(() => playersStore.players);

const setBackground = async () => {
  const background = await dialogStore.prompt(t('prompt.background'));
  if (background || background === '') {
    userPreferences.background = background;
  }
};

const imageOptIn = async () => {
  if (userPreferences.isImageOptIn || (await dialogStore.confirm(t('prompt.imageOptIn')))) {
    userPreferences.isImageOptIn = !userPreferences.isImageOptIn;
  }
};

const supportedLangs = locale.supportedLanguages;

const previousLanguage = () => {
  const currentIndex = supportedLangs.indexOf(locale.currentLanguage);
  const prevIndex = (currentIndex - 1 + supportedLangs.length) % supportedLangs.length;
  const targetLang = supportedLangs[prevIndex];

  if (targetLang) {
    locale.forceLocale(targetLang);
  }
};

const nextLanguage = () => {
  const currentIndex = supportedLangs.indexOf(locale.currentLanguage);
  const nextIndex = (currentIndex + 1) % supportedLangs.length;
  const targetLang = supportedLangs[nextIndex];

  if (targetLang) {
    locale.forceLocale(targetLang);
  }
};
</script>

<style lang="scss" src="./Menu.scss" scoped></style>
